import React from 'react';
import { cva } from 'class-variance-authority';
import PropTypes from 'prop-types';

import { Slot } from '@radix-ui/react-slot';

import cnMerge from '../../lib/utils';

const buttonVariants = cva(
    [
        'inline-flex items-center justify-center whitespace-nowrap rounded-md',
        'text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'focus-visible:ring-offset-2 ring-offset-background',
        'disabled:pointer-events-none disabled:opacity-50'
    ].join(' '),
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground hover:bg-primary/90',
                destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                outline:
                    'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
                secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
                ghost: 'hover:bg-accent hover:text-accent-foreground',
                link: 'text-primary underline-offset-4 hover:underline'
            },
            size: {
                default: 'h-10 px-4 py-2',
                sm: 'h-9 rounded-md px-3',
                lg: 'h-11 rounded-md px-8',
                icon: 'h-10 w-10'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

const Button = React.forwardRef(({ className, variant, size, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            ref={ref}
            className={cnMerge(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );
});

Button.displayName = 'Button';

Button.propTypes = {
    className: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'destructive', 'outline', 'secondary', 'ghost', 'link']),
    size: PropTypes.oneOf(['default', 'sm', 'lg', 'icon']),
    asChild: PropTypes.bool
};

Button.defaultProps = {
    className: undefined,
    variant: undefined,
    size: undefined,
    asChild: false
};

export { Button, buttonVariants };
